// LIBRERIE/IMPORTS
import React from "react";
import { Helmet } from "react-helmet";
import { Accordion } from 'react-bootstrap';
import { useTranslation } from "react-i18next";

// HOOKS
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";

// COMPONENTI
import SubHeaderDefault from "../Header/subHeader/SubHeaderDefault";
import WidgetContacts from "../Widgets/WidgetContacts/WidgetContacts";
import WidgetPartners from "../Widgets/WidgetPartners/WidgetPartners";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import ServerError from "../Error/ServerError";

// CONFIGURAZIONI
import { appConfig, AGENCY_ID } from "../../config/appConfig";

// FOGLI DI STILE
import styles from './Services.module.css';

const Services: React.FC = () => {
    const { t } = useTranslation();
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(AGENCY_ID);

    const sellerServices = Array.from({ length: 18 }, (_, i) => ({
        id: `seller-service-${i + 1}`,
        titleKey: `servicesPage.forSeller.item${i + 1}.title`,
        descKey: `servicesPage.forSeller.item${i + 1}.desc`
    }));

    const serviceImages = [
        { src: '/assets/images/services/image-1.jpg', alt: t('servicesPage.images.alt1') },
        { src: '/assets/images/services/image-2.jpg', alt: t('servicesPage.images.alt2') },
        { src: '/assets/images/services/image-3.jpg', alt: t('servicesPage.images.alt3') },
        { src: '/assets/images/services/image-4.jpg', alt: t('servicesPage.images.alt4') },
        { src: '/assets/images/services/image-5.jpg', alt: t('servicesPage.images.alt5') },
        { src: '/assets/images/services/image-6.jpg', alt: t('servicesPage.images.alt6') },
        { src: '/assets/images/services/image-7.jpg', alt: t('servicesPage.images.alt7') },
        { src: '/assets/images/services/image-8.jpg', alt: t('servicesPage.images.alt8') },
    ];

    const allPartnersForWidget = appConfig.content.partners;

    const renderTextWithBreaks = (textKey: string) => {
        const text = t(textKey);
        if (typeof text !== 'string') {
            console.warn(`Translation key ${textKey} did not return a string.`);
            return text;
        }
        return text.split('\n').map((line, index, arr) => (
            <React.Fragment key={index}>
                {line}
                {index < arr.length - 1 && <br />}
            </React.Fragment>
        ));
    };


    if (agencyLoading) return <LoadingIndicator />;
    if (agencyError) return <ServerError />;

    return (
        <>

            <Helmet>
                <title>{t('navigation.servicesLinkTitle')} | {agency?.agencyName ?? t('common.defaultAgencyName')}</title>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Lato:wght@300;400&display=swap" rel="stylesheet" />
            </Helmet>

            <SubHeaderDefault title={t('navigation.servicesLinkTitle')} isSeo={true} />

            <section className="space-ptb">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-8 mb-5 mb-lg-0">
                            <div className={styles.servicesContent}>

                                {/* --- INTRODUZIONE --- */}
                                <h3>{t('servicesPage.intro.title')}</h3>
                                <p className={`${styles.firstParagraph} mb-4`}>{t('servicesPage.intro.p1')}</p>
                                <p className="mb-4">{t('servicesPage.intro.p2')}</p>
                                <p>{t('servicesPage.intro.p3')}</p>

                                {serviceImages[0] && (
                                    <img
                                        src={serviceImages[0].src}
                                        alt={serviceImages[0].alt}
                                        className={styles.sectionImage}
                                    />
                                )}


                                <hr className={styles.sectionDivider} />

                                {/* --- SERVIZI PER IL VENDITORE (ACCORDION) --- */}
                                <h3>{t('servicesPage.forSeller.title')}</h3>

                                <Accordion defaultActiveKey="0" className={styles.serviceAccordion}>
                                    {sellerServices.map((service, index) => (
                                        <Accordion.Item eventKey={String(index)} key={service.id}>
                                            <Accordion.Header>{t(service.titleKey)}</Accordion.Header>
                                            <Accordion.Body>
                                                {t(service.descKey)}
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>

                                <hr className={styles.sectionDivider} />

                                {/* --- SERVIZI PER L'ACQUIRENTE --- */}
                                {serviceImages[1] && (
                                    <img
                                        src={serviceImages[1].src}
                                        alt={serviceImages[1].alt}
                                        className={styles.sectionImage}
                                    />
                                )}
                                <h3>{t('servicesPage.forBuyer.title')}</h3>
                                <p className="mb-4">{t('servicesPage.forBuyer.intro')}</p>

                                <div className={styles.buyerServiceBlock}>
                                    <h4>{t('servicesPage.forBuyer.wideRange.title')}</h4>
                                    <p>{t('servicesPage.forBuyer.wideRange.desc')}</p>
                                </div>
                                <div className={styles.buyerServiceBlock}>
                                    <h4>{t('servicesPage.forBuyer.certified.title')}</h4>
                                    <p>{t('servicesPage.forBuyer.certified.desc')}</p>
                                </div>
                                <div className={styles.buyerServiceBlock}>
                                    <h4>{t('servicesPage.forBuyer.privacy.title')}</h4>
                                    <p>{t('servicesPage.forBuyer.privacy.desc')}</p>
                                </div>
                                <div className={styles.buyerServiceBlock}>
                                    <h4>{t('servicesPage.forBuyer.assistance.title')}</h4>
                                    <p>{t('servicesPage.forBuyer.assistance.desc')}</p>
                                </div>
                                <div className={styles.buyerServiceBlock}>
                                    <h4>{t('servicesPage.forBuyer.postSale.title')}</h4>
                                    <p>{t('servicesPage.forBuyer.postSale.desc')}</p>
                                </div>

                                <hr className={styles.sectionDivider} />

                                {/* --- ALTRI SERVIZI (CARDS/SEZIONI) --- */}
                                {serviceImages[2] && (
                                    <img
                                        src={serviceImages[2].src}
                                        alt={serviceImages[2].alt}
                                        className={styles.sectionImage}
                                    />
                                )}
                                <h3>{t('servicesPage.otherServices.title')}</h3>
                                <p className="mb-4">{t('servicesPage.otherServices.intro')}</p>

                                {/* Card Locazioni */}
                                <div className={styles.otherServiceCard}>
                                    {serviceImages[3] && (
                                        <img
                                            src={serviceImages[3].src}
                                            alt={serviceImages[3].alt}
                                            className={`${styles.sectionImage} ${styles.cardImageTop}`}
                                        />
                                    )}
                                    <h4>{t('servicesPage.otherServices.locations.title')}</h4>
                                    <p>{t('servicesPage.otherServices.locations.desc')}</p>
                                    <p className="mt-3 small text-muted">{renderTextWithBreaks('servicesPage.otherServices.locations.fee')}</p>
                                </div>

                                {/* Card Cessione */}
                                <div className={styles.otherServiceCard}>
                                     {serviceImages[4] && (
                                        <img
                                            src={serviceImages[4].src}
                                            alt={serviceImages[4].alt}
                                            className={`${styles.sectionImage} ${styles.cardImageTop}`}
                                        />
                                    )}
                                    <h4>{t('servicesPage.otherServices.businessTransfer.title')}</h4>
                                    <p>{t('servicesPage.otherServices.businessTransfer.desc')}</p>
                                    <p className="mt-3 small text-muted">{t('servicesPage.otherServices.businessTransfer.fee')}</p>
                                </div>

                                {/* Card Stime */}
                                <div className={styles.otherServiceCard}>
                                     {serviceImages[5] && (
                                        <img
                                            src={serviceImages[5].src}
                                            alt={serviceImages[5].alt}
                                            className={`${styles.sectionImage} ${styles.cardImageTop}`}
                                        />
                                    )}
                                    <h4>{t('servicesPage.otherServices.estimates.title')}</h4>
                                    <p>{renderTextWithBreaks('servicesPage.otherServices.estimates.desc')}</p>
                                    <h6 className="mt-3">{t('servicesPage.otherServices.estimates.feeTitle')}</h6>
                                    <ul className={`${styles.feeList} small text-muted`}>
                                        <li>{t('servicesPage.otherServices.estimates.fee1')}</li>
                                        <li>{t('servicesPage.otherServices.estimates.fee2')}</li>
                                        <li>{t('servicesPage.otherServices.estimates.fee3')}</li>
                                    </ul>
                                </div>

                                {/* Card Mutui */}
                                <div className={styles.otherServiceCard}>
                                     {serviceImages[6] && (
                                        <img
                                            src={serviceImages[6].src}
                                            alt={serviceImages[6].alt}
                                            className={`${styles.sectionImage} ${styles.cardImageTop}`}
                                        />
                                    )}
                                    <h4>{t('servicesPage.otherServices.mortgages.title')}</h4>
                                    <p>{t('servicesPage.otherServices.mortgages.desc')}</p>
                                    <p className="mt-3">{t('servicesPage.otherServices.mortgages.partnersIntro')}</p>
                                    <p>
                                        <i className="bi bi-link-45deg me-2"></i><a href="https://www.euroansa.it/" className={styles.partnerLink} target="_blank" rel="noopener noreferrer">{t('servicesPage.otherServices.mortgages.partner1')}</a><br />
                                        <i className="bi bi-link-45deg me-2"></i><a href="https://www.affida.credit/" className={styles.partnerLink} target="_blank" rel="noopener noreferrer">{t('servicesPage.otherServices.mortgages.partner2')}</a>
                                    </p>
                                    <p className="mt-3">{t('servicesPage.otherServices.mortgages.freeServiceIntro')}</p>
                                    <ul className={`${styles.bulletList} small`}>
                                        {t('servicesPage.otherServices.mortgages.freeServicePoints').split('\n').map((point, i) => <li key={i}>{point}</li>)}
                                    </ul>
                                </div>

                                {/* Card Servizi Tecnici */}
                                <div className={styles.otherServiceCard}>
                                     {serviceImages[7] && (
                                        <img
                                            src={serviceImages[7].src}
                                            alt={serviceImages[7].alt}
                                            className={`${styles.sectionImage} ${styles.cardImageTop}`}
                                        />
                                    )}
                                    <h4>{t('servicesPage.otherServices.technicalAdmin.title')}</h4>
                                    <ul className={`${styles.bulletList}`}>
                                        {t('servicesPage.otherServices.technicalAdmin.desc').split('\n').map((point, i) => <li key={i}>{point}</li>)}
                                    </ul>
                                </div>

                                {/* --- NOTE IMPORTANTI --- */}
                                <div className={styles.notesSection}>
                                    <h4>{t('servicesPage.notes.title')}</h4>
                                    <p>{t('servicesPage.notes.desc')}</p>
                                </div>

                            </div>
                        </div>

                        <div className="col-lg-4">
                            <div className="sidebar">
                                <WidgetContacts
                                    agencyDataAddress={agency?.address_details?.address ?? ''}
                                    agencyDataCity={agency?.address_details?.city_name ?? ''}
                                    agencyDataComuneCapoluogo={false}
                                    agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                                    agencyDataEmail={appConfig.agency.agencyEmail}
                                    agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                                    agencyDataPhone1={agency?.phone1 ?? ''}
                                    agencyDataPhone2={agency?.phone2 ?? ''}
                                    agencyDataInternationalPrefix="+39"
                                    agencyDataMobile={agency?.mobile ?? ''}
                                    // agencyDataMobile2={agency?.mobile2 ?? ''} // Opzionale
                                    // agencyDataFax={agency?.fax ?? ''} // Opzionale
                                    // agencyDataWhatsapp="123456789" // Da configurare
                                    // Dati social da configurare o passare dinamicamente
                                    agencyDataFacebook="#"
                                    agencyDataInstagram="#"
                                    agencyDataTwitter="#"
                                    agencyDataLinkedin="#"
                                    agencyDataPinterest="#"
                                />
                                <WidgetPartners
                                    partners={allPartnersForWidget}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

        </>
    );
};

export default Services;